import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
  Link,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const API = process.env.REACT_APP_API_URL;

export default function PasoResumen({ data, back, update, reset }) {
  const [loading, setLoading] = useState(false);
  const [finalizado, setFinalizado] = useState(false);  // Nueva variable de estado para manejar el flujo de "Finalizado"
  const [radicado, setRadicado] = useState(null);

  /* ======================================================
     🔹 FINALIZAR AFILIACIÓN (GUARDAR EN MONGO)
  ====================================================== */
  const finalizar = async () => {
    if (!data.declaraciones) {
      alert("Debes aceptar los términos y condiciones");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(
        `${API}/api/affiliation/finish`,
        data
      );

      setRadicado(res.data.radicado);
      setFinalizado(true);

    } catch (err) {
      console.error("❌ Error finalizando afiliación:", err);
      alert(
        err.response?.data?.message ||
        "Ocurrió un error al finalizar la afiliación"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ======================================================
     🔹 HELPERS UI
  ====================================================== */
  const formatDate = (date) => {
    if (!date) return "—";
    const [y, m, d] = date.split("-");
    return `${d}/${m}/${y}`;
  };

  const formatMoney = (num) => {
    if (!num || isNaN(num)) return "—";
    return Number(num).toLocaleString("es-CO");
  };

  const Item = ({ label, value }) => (
    <Grid item xs={12} sm={6}>
      <Typography fontSize="0.8rem" color="#777">
        {label}
      </Typography>
      <Typography fontWeight={600} fontSize="1rem" mb={1}>
        {value || "—"}
      </Typography>
    </Grid>
  );

  const Section = ({ title, children }) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 3,
        border: "1px solid #e5e5e5",
        background: "#fff",
      }}
    >
      <Typography fontWeight={700} mb={2}>
        {title}
      </Typography>
      {children}
    </Paper>
  );

  /* ======================================================
     🔹 RENDER
  ====================================================== */
  return (
    <Box>
      {/* ====================== CONFIRMACIÓN AL FINALIZAR ====================== */}
      <Typography variant="h6" fontWeight={600} mb={3} textAlign="center">
        Resumen de Información
      </Typography>

      {/* ====================== DATOS ====================== */}
      <Section title="Datos personales">
        <Grid container spacing={2}>
          <Item label="Nombres y apellidos" value={data.nombres} />
          <Item label="Tipo de identificación" value={data.tipoIdentidad} />
          <Item label="Número de identificación" value={data.numeroDocumento} />
          <Item label="Fecha de expedición" value={formatDate(data.fechaExpedicion)} />
          <Item label="Ciudad de expedición" value={data.ciudadExpedicion} />
          <Item label="Fecha de nacimiento" value={formatDate(data.fechaNacimiento)} />
          <Item label="Lugar de nacimiento" value={data.lugarNacimiento} />
          <Item label="Género" value={data.genero} />
          <Item label="Grupo étnico" value={data.grupoEtnico} />
          <Item label="Nivel de estudios" value={data.nivelEstudios} />
          <Item label="Título profesional" value={data.tituloProfesional} />
        </Grid>
      </Section>

      <Section title="Información de contacto">
        <Grid container spacing={2}>
          <Item label="Departamento" value={data.departamento} />
          <Item label="Municipio" value={data.municipio} />
          <Item label="Barrio" value={data.barrio} />
          <Item label="Dirección" value={data.direccion} />
          <Item label="Teléfono" value={data.telefonoFijo} />
          <Item label="WhatsApp" value={data.celular} />
          <Item label="Correo" value={data.correoPersonal} />
        </Grid>
      </Section>

      <Section title="Información familiar">
        <Grid container spacing={2}>
          <Item label="Cabeza de familia" value={data.cabezaFamilia} />
          <Item label="Personas a cargo" value={data.personasCargo} />
          <Item label="Estado civil" value={data.estadoCivil} />
          <Item label="Tipo de vivienda" value={data.tipoVivienda} />
          <Item label="Estrato" value={data.estrato} />
        </Grid>
      </Section>

      <Section title="Información laboral">
        <Grid container spacing={2}>
          <Item label="Empresa" value={data.empresaContratante} />
          <Item label="Actividad económica" value={data.actividadEconomica} />
          <Item label="CIIU" value={data.codigoCIIU} />
        </Grid>
      </Section>

      <Section title="Ahorros">
        <Grid container spacing={2}>
          <Item label="Ahorro Obligatorio" value={formatMoney(data.ahorroObligatorio)} />
          <Item label="Ahorro a la Vista" value={formatMoney(data.aLaVista)} />
          <Item label="Ahorro Navideño" value={formatMoney(data.navideno)} />
          <Item label="Ahorro Vehículo" value={formatMoney(data.vehiculo)} />
          <Item label="Ahorro Vivienda" value={formatMoney(data.vivienda)} />
        </Grid>
      </Section>

      <Section title="Beneficiarios">
        {Array.isArray(data.beneficiarios) && data.beneficiarios.length > 0 ? (
          data.beneficiarios.map((b, idx) => (
            <Grid container spacing={2} key={idx} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Typography fontWeight={600} fontSize="0.9rem" color="#444">
                  Beneficiario {idx + 1}
                </Typography>
              </Grid>

              <Item label="Nombres y apellidos" value={b.nombre || b.nombres} />
              <Item label="Número de identifiación" value={b.id} />
              <Item label="Porcentaje del auxilio" value={b.porcentaje ? `${b.porcentaje}%` : "—"} />
            </Grid>
          ))
        ) : (
          <Typography fontSize="0.9rem" color="#777">
            No registraste beneficiarios.
          </Typography>
        )}
      </Section>

      <Section title="Referencias">
        {Array.isArray(data.referencias) && data.referencias.length > 0 ? (
          data.referencias.map((r, idx) => (
            <Grid container spacing={2} key={idx} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Typography fontWeight={600} fontSize="0.9rem" color="#444">
                  Referencia {idx + 1}
                </Typography>
              </Grid>

              <Item label="Nombres y apellidos" value={r.nombre} />
              <Item label="Parentesco" value={r.parentesco} />
              <Item label="Número de celular" value={r.telefono} />
            </Grid>
          ))
        ) : (
          <Typography fontSize="0.9rem" color="#777">
            No registraste referencias.
          </Typography>
        )}
      </Section>

      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={data.declaraciones || false}
              disabled={finalizado}  // Bloquear checkbox si ya finalizó
              onChange={(e) =>
                update({ declaraciones: e.target.checked })
              }
            />
          }
          label={
            <Typography fontSize="0.9rem">
              Declaro que la información suministrada es verdadera, completa y exacta. Acepto los{" "}
              <Link
                href="https://fondomanpower.com/project/tc-afiliacion/"
                target="_blank"
                sx={{ marginRight: '4px' }}
              >
                términos y condiciones
              </Link>
              del proceso de afiliación, así como las políticas de privacidad y autorizo el tratamiento de mis datos conforme a la normativa vigente.
            </Typography>
          }
        />
      </Paper>

      {finalizado && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            textAlign: "center"
          }}
        >
          <WarningAmberIcon assBottomIcon sx={{ fontSize: 48, color: "#16a34a", mb: 1 }} />

          <Typography variant="h6" fontWeight={700} mb={1}>
            ¡Bienvenido, solo falta un paso!
          </Typography>

          {radicado && (
            <Typography
              fontSize="1rem"
              fontWeight={600}
              sx={{ color: "#15803d", mb: 1 }}
            >
              Tu radicado es: {radicado}
            </Typography>
          )}

          <Typography fontSize="0.95rem" color="#444">
            Para activar todos tus beneficios, completa tu afiliación hoy mismo firmando digitalmente en el enlace que hemos enviado a tu número de WhatsApp.
          </Typography>
        </Paper>
      )}

      <Box display="flex" justifyContent="center" gap={3} mt={4}>
        {/* Solo mostrar estos botones si no está finalizado */}
        {!finalizado ? (
          <>
            <Button
              variant="outlined"
              size="large"
              onClick={back}
              sx={{
                borderRadius: "20px",
                px: 4,
                textTransform: "none",
                fontSize: "1rem"
              }}
            >
              Atrás
            </Button>

            <Button
              variant="contained"
              size="large"
              sx={{
                borderRadius: "20px",
                px: 4,
                textTransform: "none",
                fontSize: "1rem"
              }}
              disabled={!data.declaraciones || loading}
              onClick={finalizar}
            >
              {loading ? "Enviando..." : "Finalizar"}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            size="large"
            onClick={reset}
            sx={{
              borderRadius: "20px",
              px: 4,
              textTransform: "none",
              fontSize: "1rem"
            }}
          >
            Volver al inicio
          </Button>
        )}
      </Box>
    </Box>
  );
}
